import React, { useMemo } from 'react';
import { toast } from 'sonner';
import { CreditCard } from 'lucide-react';
import { mockPayments } from '@shared/data';
import { SectionCard } from '../../components/sections/SectionCard';
import { PanelTable } from '../../components/display/PanelTable';
import { RiskBadge } from '@shared/components/ui/RiskBadge';
import { transformPaymentData } from '@shared/lib/tables/transformers/payment-transformer';
import {
  CurrencyCell,
  TextCell,
  DynamicDateCell,
  IconTextCell,
  TruncatedCell,
} from '@shared/lib/tables/cells';
import type { Payment } from '@shared/types/payment.types';
import type { PanelTableColumn } from '../../components/display/PanelTable';
import type { TableAction } from '@shared/types/data-table';

// ─── Default data ────────────────────────────────────────────────────────────

const DEFAULT_RELATED_PAYMENTS = mockPayments.slice(10, 13);

// ─── Column Definitions ───────────────────────────────────────────────────────

const COLUMNS: PanelTableColumn<Payment>[] = [
  {
    key: 'riskScore',
    label: 'RISK',
    headerClassName: 'w-10',
    render: (_value, payment) => {
      const score = parseInt(String(payment.riskScore ?? '0'), 10);
      return <RiskBadge score={score} />;
    },
  },
  {
    key: 'amount',
    label: 'Amount',
    headerClassName: 'w-[120px]',
    cellClassName: 'whitespace-nowrap',
    render: (_value, payment) => {
      const data = transformPaymentData(payment);
      return (
        <CurrencyCell
          amount={data.amount}
          currency={data.currency}
          decimals={data.decimals}
          align="right"
        />
      );
    },
  },
  {
    key: 'customer',
    label: 'Customer',
    headerClassName: 'w-[160px]',
    render: (_value, payment) => {
      const data = transformPaymentData(payment);
      return <TextCell value={data.customer} maxWidth={160} />;
    },
  },
  {
    key: 'description',
    label: 'Description',
    headerClassName: 'w-[200px]',
    render: (_value, payment) => {
      const data = transformPaymentData(payment);
      return <TextCell value={data.description} maxWidth={200} />;
    },
  },
  {
    key: 'paymentMethod',
    label: 'Payment method',
    headerClassName: 'w-[140px]',
    render: (_value, payment) => {
      const data = transformPaymentData(payment);
      if (data.isCardPayment) {
        return (
          <IconTextCell
            icon={<CreditCard className="w-3 h-3" />}
            iconClassName="text-muted-foreground"
          >
            <TruncatedCell maxWidth={140} className="text-muted-foreground">
              {data.paymentMethod}
            </TruncatedCell>
          </IconTextCell>
        );
      }
      return (
        <TruncatedCell maxWidth={140} className="text-muted-foreground">
          {data.paymentMethod}
        </TruncatedCell>
      );
    },
  },
  {
    key: 'channel',
    label: 'Channel',
    headerClassName: 'w-[160px]',
    render: (_value, payment) => {
      const data = transformPaymentData(payment);
      const display = data.channel && data.channelId
        ? `${data.channel} (${data.channelId})`
        : data.channel;
      return <TextCell value={display} maxWidth={160} />;
    },
  },
  {
    key: 'createdAt',
    label: 'Created',
    hideInPanel: true,
    headerClassName: 'w-[150px]',
    cellClassName: 'w-[150px] text-right',
    render: (_value, payment) => {
      const data = transformPaymentData(payment);
      return <DynamicDateCell timestamp={data.createdAt} />;
    },
  },
];

// ─── Row Actions ──────────────────────────────────────────────────────────────

function createRowActions(
  onView?: (payment: Payment) => void,
): (payment: Payment) => TableAction<Payment>[] {
  return (payment) => [
    {
      key: 'view-details',
      label: 'View details',
      onClick: () => onView?.(payment),
    },
    {
      key: 'refund',
      label: 'Refund payment',
      onClick: () => { toast.info('Refund functionality coming soon'); },
      condition: (p: Payment) => p.status === 'succeeded',
    },
    {
      key: 'capture',
      label: 'Capture payment',
      onClick: () => { toast.info('Capture functionality coming soon'); },
      condition: (p: Payment) => p.status === 'uncaptured',
    },
    {
      key: 'cancel',
      label: 'Cancel payment',
      onClick: () => { toast.info('Cancel functionality coming soon'); },
      condition: (p: Payment) => p.status === 'uncaptured' || p.status === 'pending',
      variant: 'destructive' as const,
    },
  ];
}

// ─── Component ────────────────────────────────────────────────────────────────

interface RelatedPaymentsProps {
  isExpanded?: boolean;
  onToggle?: () => void;
  isDetailView?: boolean;
  payments?: Payment[];
  onViewPayment?: (payment: Payment) => void;
  className?: string;
}

export const RelatedPayments = React.memo<RelatedPaymentsProps>(function RelatedPayments({
  isExpanded = true,
  onToggle,
  isDetailView = false,
  payments,
  onViewPayment,
  className,
}) {
  const data = useMemo(() => payments ?? DEFAULT_RELATED_PAYMENTS, [payments]);
  const rowActions = useMemo(() => createRowActions(onViewPayment), [onViewPayment]);

  return (
    <SectionCard
      title="Related payments"
      layout="down"
      isCollapsible={Boolean(onToggle)}
      isCollapsed={!isExpanded}
      onTitleClick={onToggle}
      className={className}
    >
      <PanelTable<Payment>
        data={data}
        columns={COLUMNS}
        variant={isDetailView ? 'full' : 'panel'}
        onRowClick={onViewPayment}
        rowActions={rowActions}
        className="[&_thead]:bg-transparent [&_tbody]:bg-transparent"
      />
    </SectionCard>
  );
});

RelatedPayments.displayName = 'RelatedPayments';
